import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from 'assets/actionPlan/Dashboard/close.svg';
import FilterActiveIcon from 'assets/filter-active.svg';
import FilterIcon from 'assets/filter.svg';
import CustomDatePicker from 'common-ui/customDatePicker';
import { AsyncSelect } from 'common-ui/select';

import { getDepartments } from '../../../features/actions/actionPlanActions';
import { selectDepartmentsList } from '../../../features/actions/actionPlanSlice';
import { selectUserInfo } from '../../../features/auth/authSlice';
import { ROLES, getUserRole } from '../dashboard/table/dashboardPermissions';
import {
  ClearAllButton,
  CloseImg,
  DataRangeBlock,
  DateRangeGroup,
  ExpandedFilters,
  FilterFieldsGroup,
  FilterImg,
  FilterToggleButton,
  FilterWrapper,
  PeriodTab,
  PeriodTabs,
  TopFilterRow,
} from './ActionFilter.styles';

const ActionFilter = ({ filters, employees, actions, onFilterChange, onClearAll }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const allDepartments = useSelector(selectDepartmentsList);

  const periods = ['Daily', 'Weekly', 'Monthly'];

  const handleDepartmentMenuOpen = () => {
    if (!allDepartments || allDepartments.length === 0) {
      dispatch(getDepartments());
    }
  };

  const formatOptions = (data, labelKey = 'name') =>
    data?.map((item) => ({
      value: item.id || item.uuid,
      label: item[labelKey] || item.fullName || item.name,
    })) || [];

  const formatEmployeeOptions = (data) =>
    data?.map((item) => ({
      value: item.id || item.uuid,
      label: `${item.name} ${item.surname}`.trim(),
    })) || [];

  const user = useSelector(selectUserInfo);
  const userRole = getUserRole(user);

  const userDepartmentCount = user?.departmentIds?.length || 0;

  const departmentOptions = React.useMemo(
    () => formatOptions(allDepartments, 'name'),
    [allDepartments]
  );

  const actionOptions = React.useMemo(() => formatOptions(actions, 'actionName'), [actions]);

  const employeeOptions = React.useMemo(() => formatEmployeeOptions(employees), [employees]);
  const isFiltersDisabled = filters.tabMode === 'DEPARTMENT' && !filters.departmentId;

  const getFilterVisibility = () => {
    const isDepartmentTab = filters.tabMode === 'DEPARTMENT';

    if (userRole === ROLES.TEAM_MEMBER) {
      return {
        showEmployee: false,
        showDepartment: false,
        showAction: true,
      };
    }

    if (userRole === ROLES.TEAM_LEAD || userRole === ROLES.BRANCH_HEAD) {
      return {
        showEmployee: true,
        showDepartment: false,
        showAction: true,
      };
    }

    if (userRole === ROLES.DEPARTMENT_HEAD && isDepartmentTab) {
      return {
        showEmployee: false,
        showDepartment: true,
        showAction: true,
      };
    } else if (userRole === ROLES.DEPARTMENT_HEAD && !isDepartmentTab) {
      return {
        showEmployee: true,
        showDepartment: false,
        showAction: true,
      };
    }
    if (userRole === ROLES.GENERAL_MANAGER || userRole === ROLES.SUPER_ADMIN) {
      if (isDepartmentTab) {
        return {
          showEmployee: false,
          showDepartment: true,
          showAction: true,
        };
      }
      return {
        showEmployee: true,
        showDepartment: false,
        showAction: true,
      };
    }
    if (
      userRole === ROLES.HR_MANAGER ||
      userRole === ROLES.LEGAL_STAFF ||
      userRole === ROLES.ACCOUNTING_STAFF
    ) {
      return {
        showEmployee: false,
        showDepartment: false,
        showAction: true,
      };
    }

    return {
      showEmployee: true,
      showDepartment: true,
      showAction: true,
    };
  };

  const filterVisibility = getFilterVisibility();

  return (
    <FilterWrapper>
      <TopFilterRow $isOpen={isOpen}>
        <FilterToggleButton onClick={() => setIsOpen(!isOpen)} $active={isOpen}>
          <FilterImg src={isOpen ? FilterActiveIcon : FilterIcon} alt="filter" />
          Filter
        </FilterToggleButton>

        <PeriodTabs>
          {periods.map((p) => (
            <PeriodTab
              key={p}
              $active={filters.period === p.toUpperCase()}
              onClick={() => onFilterChange('period', p.toUpperCase())}
            >
              {p}
            </PeriodTab>
          ))}
        </PeriodTabs>
      </TopFilterRow>

      {isOpen && (
        <ExpandedFilters>
          <CloseImg src={CloseIcon} alt="close" onClick={() => setIsOpen(false)} />
          <FilterFieldsGroup>
            {filterVisibility.showEmployee && (
              <AsyncSelect
                key={`emp-${filters.employeeId || 'empty'}`}
                placeholder="Select Employee"
                defaultOptions
                loadOptions={(inputValue, callback) => {
                  callback(
                    employeeOptions.filter((opt) =>
                      opt.label.toLowerCase().includes((inputValue || '').toLowerCase())
                    )
                  );
                }}
                value={
                  filters.employeeId
                    ? employeeOptions.find(
                        (opt) => String(opt.value) === String(filters.employeeId)
                      )
                    : null
                }
                onChange={(option) =>
                  onFilterChange('employeeId', option ? option.value : null)
                }
                isClearable
              />
            )}

            {filterVisibility.showDepartment && (
              <AsyncSelect
                key={`dept-${filters.departmentId || 'empty'}`}
                placeholder="Select Department"
                defaultOptions
                loadOptions={(inputValue, callback) => {
                  callback(
                    departmentOptions.filter((opt) =>
                      opt.label.toLowerCase().includes((inputValue || '').toLowerCase())
                    )
                  );
                }}
                value={
                  filters.departmentId
                    ? departmentOptions.find(
                        (opt) => String(opt.value) === String(filters.departmentId)
                      )
                    : null
                }
                onChange={(option) =>
                  onFilterChange('departmentId', option ? option.value : null)
                }
                onMenuOpen={handleDepartmentMenuOpen}
                isClearable
              />
            )}

            {filterVisibility.showAction && (
              <AsyncSelect
                key={`action-${filters.actionId || 'empty'}-${filters.departmentId || 'all'}-${actions?.length || 0}`}
                placeholder="Select Action"
                isDisabled={isFiltersDisabled}
                defaultOptions
                loadOptions={(inputValue, callback) => {
                  callback(
                    actionOptions.filter((opt) =>
                      opt.label.toLowerCase().includes((inputValue || '').toLowerCase())
                    )
                  );
                }}
                value={
                  filters.actionId
                    ? actionOptions.find(
                        (opt) => String(opt.value) === String(filters.actionId)
                      )
                    : null
                }
                onChange={(option) => onFilterChange('actionId', option ? option.value : null)}
                isClearable
              />
            )}

            <DateRangeGroup>
              <DataRangeBlock>
                <CustomDatePicker
                  placeholder="From"
                  disabled={isFiltersDisabled}
                  value={filters.from}
                  onChange={(date) => onFilterChange('from', date)}
                  onClear={() => onFilterChange('from', '')}
                />
                <CustomDatePicker
                  placeholder="To"
                  disabled={isFiltersDisabled}
                  value={filters.to}
                  onChange={(date) => onFilterChange('to', date)}
                  onClear={() => onFilterChange('to', '')}
                />
              </DataRangeBlock>
            </DateRangeGroup>
          </FilterFieldsGroup>

          <ClearAllButton onClick={onClearAll}>Clear all</ClearAllButton>
        </ExpandedFilters>
      )}
    </FilterWrapper>
  );
};

export default ActionFilter;
